const configElement = document.querySelector('[data-ac-wishlist-config]');
let config;
try { config = JSON.parse(configElement?.textContent || 'null'); } catch {}

if (config && !window.ACWishlist) {
  const strings = config.strings || {};
  const text = (key, fallback = '') => strings[key] || fallback;
  const prefix = `ac:wishlist:v1:${config.shop || location.hostname}:`;
  const pages = new Map();
  const cache = new Map();
  let lastRemoval = null;
  let readFailed = false;
  let feedback;
  let feedbackReturn;
  let feedbackTimer;
  let refreshFrame;
  const heartPath = 'M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8Z';

  function rootURL() {
    const url = new URL(config.root || '/', location.origin);
    if (url.origin !== location.origin) return new URL('/', location.origin);
    if (!url.pathname.endsWith('/')) url.pathname += '/';
    return url;
  }

  function storeURL(path) {
    const url = new URL(path, rootURL());
    const current = new URL(location.href);
    for (const key of ['preview_theme_id', '_ab', '_fd', '_sc']) {
      if (current.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
    }
    return url;
  }

  function imageURL(value) {
    if (!value) return '';
    try {
      const url = new URL(value, location.origin);
      if (!['https:', 'http:'].includes(url.protocol)) return '';
      if (url.protocol !== 'https:' && url.origin !== location.origin) return '';
      if (url.origin !== location.origin && url.hostname !== 'cdn.shopify.com') return '';
      return url.href;
    } catch { return ''; }
  }

  function cleanItem(item) {
    if (!item || typeof item !== 'object') return null;
    const id = String(item.id || '');
    const handle = String(item.handle || '');
    if (!/^\d{1,24}$/.test(id) || !/^[\p{L}\p{N}][\p{L}\p{N}_-]{0,254}$/u.test(handle)) return null;
    return {
      id,
      handle,
      title: String(item.title || '').slice(0, 300),
      vendor: String(item.vendor || '').slice(0, 160),
      image: imageURL(item.image),
      savedAt: Number.isFinite(Number(item.savedAt)) ? Number(item.savedAt) : Date.now()
    };
  }

  function getItems() {
    const items = [];
    readFailed = false;
    try {
      const storage = window.localStorage;
      for (let index = 0; index < storage.length; index += 1) {
        const key = storage.key(index);
        if (!key?.startsWith(prefix)) continue;
        const raw = storage.getItem(key);
        if (!raw || raw.length > 12000) continue;
        try {
          const item = cleanItem(JSON.parse(raw));
          if (item && key === prefix + item.id) items.push(item);
        } catch {}
      }
    } catch { readFailed = true; }
    return items.sort((a, b) => b.savedAt - a.savedAt || a.id.localeCompare(b.id));
  }

  function node(tag, className, content) {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (content !== undefined) element.textContent = content;
    return element;
  }

  function heart() {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('width', '22');
    svg.setAttribute('height', '22');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('aria-hidden', 'true');
    const path = document.createElementNS(svg.namespaceURI, 'path');
    path.setAttribute('d', heartPath);
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '1.5');
    path.setAttribute('stroke-linejoin', 'round');
    svg.append(path);
    return svg;
  }

  function dismissFeedback(restoreFocus = false) {
    clearTimeout(feedbackTimer);
    if (!feedback) return;
    const hadFocus = feedback.contains(document.activeElement);
    feedback.hidden = true;
    lastRemoval = null;
    if (restoreFocus || hadFocus) {
      if (feedbackReturn?.isConnected && feedbackReturn.getClientRects().length) feedbackReturn.focus({ preventScroll: true });
      else document.querySelector('[data-ac-wishlist-link]')?.focus({ preventScroll: true });
    }
  }

  function feedbackElement() {
    if (feedback?.isConnected) return feedback;
    feedback = node('div', 'ac-wl-feedback');
    feedback.hidden = true;
    const message = node('span', 'ac-wl-feedback__message');
    message.dataset.acWishlistMessage = '';
    message.setAttribute('role', 'status');
    message.setAttribute('aria-live', 'polite');
    message.setAttribute('aria-atomic', 'true');
    const undoButton = node('button', 'ac-wl-feedback__undo', text('undo'));
    undoButton.type = 'button';
    undoButton.dataset.acWishlistUndo = '';
    undoButton.addEventListener('click', undo);
    const dismiss = node('button', 'ac-wl-feedback__dismiss', '×');
    dismiss.type = 'button';
    dismiss.dataset.acWishlistDismiss = '';
    dismiss.setAttribute('aria-label', text('dismiss'));
    dismiss.addEventListener('click', () => dismissFeedback(true));
    feedback.append(message, undoButton, dismiss);
    document.body.append(feedback);
    return feedback;
  }

  function announce(message, canUndo = false, error = false) {
    clearTimeout(feedbackTimer);
    const element = feedbackElement();
    if (document.activeElement !== document.body && !element.contains(document.activeElement)) feedbackReturn = document.activeElement;
    element.dataset.error = String(error);
    element.querySelector('[data-ac-wishlist-message]').textContent = message;
    element.querySelector('[data-ac-wishlist-undo]').hidden = !canUndo;
    element.hidden = false;
    if (config.productId && !error) {
      feedbackTimer = setTimeout(() => dismissFeedback(), 1500);
    }
  }

  function updateControls(items = getItems()) {
    const ids = new Set(items.map((item) => item.id));
    document.querySelectorAll('[data-ac-wishlist-toggle]').forEach((button) => {
      const saved = ids.has(button.dataset.productId);
      button.setAttribute('aria-pressed', String(saved));
      button.setAttribute('aria-label', saved ? text('remove') : text('save'));
      const label = button.querySelector('[data-ac-wishlist-label]');
      if (label) label.textContent = saved ? text('saved') : text('save');
      button.disabled = false;
    });
    document.querySelectorAll('[data-ac-wishlist-link]').forEach((link) => {
      link.setAttribute('aria-label', `${text('wishlist')}${items.length ? ` (${items.length})` : ''}`);
      const href = new URL(config.url || 'pages/wishlist', rootURL());
      if (href.origin === location.origin) link.href = storeURL(href.pathname).href;
      const count = link.querySelector('[data-ac-wishlist-header-count]');
      if (count) {
        count.textContent = items.length > 99 ? '99+' : String(items.length);
        count.hidden = !items.length;
      }
    });
  }

  function refresh() {
    const items = getItems();
    updateControls(items);
    for (const [page, state] of pages) {
      if (page.isConnected) renderPage(page, state, items);
      else { state.controller?.abort(); pages.delete(page); }
    }
    document.dispatchEvent(new CustomEvent('ac:wishlist:change', { detail: { count: items.length } }));
    return items;
  }

  function save(product, restore = false) {
    const item = cleanItem({ ...product, savedAt: restore ? product.savedAt : Date.now() });
    const items = getItems();
    if (!item || readFailed) {
      announce(text('storageError'), false, true);
      return { ok: false };
    }
    if (!items.some((saved) => saved.id === item.id) && items.length >= 200) {
      announce(text('full'), false, true);
      return { ok: false };
    }
    try {
      window.localStorage.setItem(prefix + item.id, JSON.stringify(item));
    } catch {
      announce(text('storageError'), false, true);
      return { ok: false };
    }
    lastRemoval = null;
    refresh();
    announce(text(restore ? 'restored' : 'added'));
    return { ok: true, item };
  }

  function remove(id) {
    const item = getItems().find((entry) => entry.id === String(id));
    if (readFailed) { announce(text('storageError'), false, true); return { ok: false }; }
    if (!item) return { ok: true };
    try { window.localStorage.removeItem(prefix + item.id); }
    catch { announce(text('storageError'), false, true); return { ok: false }; }
    lastRemoval = item;
    refresh();
    announce(text('removed'), true);
    return { ok: true, item };
  }

  function toggle(product) {
    const items = getItems();
    if (readFailed) { announce(text('storageError'), false, true); return { ok: false }; }
    return items.some((item) => item.id === String(product.id)) ? remove(product.id) : save(product);
  }

  function undo() {
    if (!lastRemoval) return { ok: false };
    const item = lastRemoval;
    const needsFocus = feedback?.contains(document.activeElement);
    let result;
    if (getItems().some((entry) => entry.id === item.id)) {
      lastRemoval = null;
      announce(text('restored'));
      result = { ok: true };
    } else result = save(item, true);
    if (result.ok && needsFocus) {
      const restored = [...document.querySelectorAll('[data-ac-wishlist-item]')].find((entry) => entry.dataset.acWishlistItem === item.id);
      const control = restored?.querySelector('[data-ac-wishlist-remove]') || [...document.querySelectorAll('[data-ac-wishlist-toggle]')].find((button) => button.dataset.productId === item.id && button.getClientRects().length) || document.querySelector('[data-ac-wishlist-link]');
      control?.focus({ preventScroll: true });
      feedbackReturn = control;
    }
    return result;
  }

  function money(amount) {
    try {
      return new Intl.NumberFormat(config.locale || document.documentElement.lang || 'en', {
        style: 'currency', currency: config.currency || 'USD'
      }).format(Number(amount) / 100);
    } catch { return `${config.currency || ''} ${(Number(amount) / 100).toFixed(2)}`.trim(); }
  }

  function productURL(item) {
    return storeURL(`products/${encodeURIComponent(item.handle)}`).href;
  }

  async function loadProduct(item, signal) {
    const previous = cache.get(item.id);
    if (previous && Date.now() - previous.time < 300000) return previous;
    const controller = new AbortController();
    const abort = () => controller.abort();
    signal.addEventListener('abort', abort, { once: true });
    if (signal.aborted) controller.abort();
    const timeout = setTimeout(abort, 12000);
    let result;
    try {
      const url = storeURL(`products/${encodeURIComponent(item.handle)}.js`);
      const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (response.status === 404 || response.status === 410) result = { status: 'unavailable' };
      else {
        if (!response.ok) throw new Error('Product unavailable');
        const product = await response.json();
        if (String(product.id) !== item.id || !Number.isFinite(Number(product.price))) throw new Error('Product changed');
        result = { status: 'ready', product };
      }
    } catch {
      if (signal.aborted) return null;
      result = { status: 'error' };
    } finally {
      clearTimeout(timeout);
      signal.removeEventListener('abort', abort);
    }
    result.time = Date.now();
    cache.set(item.id, result);
    return result;
  }

  function removeCard(item, button) {
    const page = button.closest('[data-ac-wishlist-page]');
    const cards = [...(page?.querySelectorAll('[data-ac-wishlist-item]') || [])];
    const index = cards.findIndex((card) => card.dataset.acWishlistItem === item.id);
    const nextId = cards[index + 1]?.dataset.acWishlistItem || cards[index - 1]?.dataset.acWishlistItem;
    const result = remove(item.id);
    if (!result.ok || !page) return;
    const next = [...page.querySelectorAll('[data-ac-wishlist-item]')].find((card) => card.dataset.acWishlistItem === nextId);
    (next?.querySelector('[data-ac-wishlist-remove]') || page.querySelector('[data-ac-wishlist-empty-heading]'))?.focus({ preventScroll: true });
    feedbackReturn = document.activeElement;
  }

  function focusedControl(cardElement) {
    if (!cardElement?.contains(document.activeElement)) return null;
    return ['[data-ac-wishlist-remove]', '.ac-wl-card__media', '.ac-wl-card__title a', '.ac-wl-card__retry', '.ac-wl-card__action'].find((selector) => cardElement.querySelector(selector) === document.activeElement) || null;
  }

  function restoreCardFocus(cardElement, selector) {
    if (!selector) return;
    (cardElement?.querySelector(selector) || cardElement?.querySelector('.ac-wl-card__title a') || cardElement?.querySelector('[data-ac-wishlist-remove]'))?.focus({ preventScroll: true });
  }

  function card(item, result, page, position = 0) {
    const product = result?.product;
    const status = result?.status || 'loading';
    const title = String(product?.title || item.title || text('missingTitle'));
    const href = productURL(item);
    const li = node('li', `ac-wl-card${status === 'loading' ? ' ac-wl-skeleton' : ''}`);
    li.dataset.acWishlistItem = item.id;
    li.dataset.state = status;
    const media = node(status === 'unavailable' ? 'div' : 'a', 'ac-wl-card__media');
    if (media.tagName === 'A') { media.href = href; media.setAttribute('aria-label', title); }
    const image = imageURL(product?.featured_image || item.image);
    if (image) {
      const img = node('img', 'ac-wl-card__image');
      const src = new URL(image);
      src.searchParams.set('width', '720');
      img.src = src.href;
      img.srcset = [240, 360, 540, 720].map((width) => { const candidate = new URL(image); candidate.searchParams.set('width', width); return `${candidate.href} ${width}w`; }).join(', ');
      img.sizes = '(max-width: 749px) calc((100vw - 54px) / 2), (max-width: 1099px) calc((100vw - 104px) / 3), (max-width: 1384px) calc((100vw - 130px) / 4), 314px';
      img.alt = title;
      img.width = 720;
      img.height = 1080;
      img.loading = position < 4 ? 'eager' : 'lazy';
      img.decoding = 'async';
      img.addEventListener('error', () => {
        img.remove();
        const placeholder = node('span', 'ac-wl-card__placeholder');
        placeholder.setAttribute('aria-hidden', 'true');
        placeholder.append(heart());
        media.append(placeholder);
      }, { once: true });
      media.append(img);
    } else {
      const placeholder = node('span', 'ac-wl-card__placeholder');
      placeholder.setAttribute('aria-hidden', 'true');
      placeholder.append(heart());
      media.append(placeholder);
    }
    const removeButton = node('button', 'ac-wl-card__remove');
    removeButton.type = 'button';
    removeButton.dataset.acWishlistRemove = '';
    removeButton.setAttribute('aria-label', `${text('remove')}: ${title}`);
    removeButton.append(heart());
    removeButton.addEventListener('click', () => removeCard(item, removeButton));
    const content = node('div', 'ac-wl-card__content');
    const vendor = String(product?.vendor || item.vendor || '');
    if (vendor) content.append(node('p', 'ac-wl-card__vendor', vendor));
    const heading = node('h2', 'ac-wl-card__title');
    const titleElement = node(status === 'unavailable' ? 'span' : 'a', '', title);
    if (titleElement.tagName === 'A') titleElement.href = href;
    heading.append(titleElement);
    content.append(heading);
    if (status === 'ready') {
      const priceRow = node('div', 'ac-wl-card__price-row');
      const formatted = money(product.price_min ?? product.price);
      priceRow.append(node('span', 'ac-wl-card__price', product.price_varies ? text('from').replace('{price}', formatted) : formatted));
      if (!product.price_varies && Number(product.compare_at_price) > Number(product.price)) {
        priceRow.append(node('s', 'ac-wl-card__compare', money(product.compare_at_price)));
      }
      content.append(priceRow);
      if (!product.available) content.append(node('p', 'ac-wl-card__availability', text('soldOut')));
    } else if (status === 'error') {
      content.append(node('p', 'ac-wl-card__availability', text('loadError')));
      const retry = node('button', 'ac-wl-card__retry', text('retry'));
      retry.type = 'button';
      retry.addEventListener('click', () => {
        cache.delete(item.id);
        renderPage(page, pages.get(page), getItems());
      });
      content.append(retry);
    } else if (status === 'unavailable') content.append(node('p', 'ac-wl-card__availability', text('unavailable')));
    else {
      const loading = node('span', 'ac-wl-skeleton__line');
      loading.setAttribute('aria-hidden', 'true');
      content.append(loading);
    }
    if (status !== 'unavailable') {
      const action = node('a', 'ac-wl-card__action', text('view'));
      action.href = href;
      content.append(action);
    }
    li.append(media, removeButton, content);
    return li;
  }

  async function renderPage(page, state, items) {
    if (!state || !page.isConnected) return;
    state.controller?.abort();
    const controller = new AbortController();
    state.controller = controller;
    const generation = ++state.generation;
    const grid = page.querySelector('[data-ac-wishlist-grid]');
    const status = page.querySelector('[data-ac-wishlist-status]');
    const empty = page.querySelector('[data-ac-wishlist-empty]');
    const error = page.querySelector('[data-ac-wishlist-error]');
    const errorMessage = page.querySelector('[data-ac-wishlist-error-message]');
    page.querySelector('[data-ac-wishlist-count]').textContent = items.length === 1 ? text('countOne') : text('countMany').replace('{count}', String(items.length));
    empty.hidden = Boolean(items.length) || readFailed;
    error.hidden = !readFailed;
    errorMessage.textContent = readFailed ? text('readError') : '';
    const visible = items.slice(0, state.limit);
    const focusedCard = document.activeElement?.closest?.('[data-ac-wishlist-item]');
    const focusedId = focusedCard?.dataset.acWishlistItem;
    const focusSelector = focusedControl(focusedCard);
    const fragment = document.createDocumentFragment();
    for (const [position, item] of visible.entries()) {
      const cached = cache.get(item.id);
      fragment.append(card(item, cached && Date.now() - cached.time < 300000 ? cached : undefined, page, position));
    }
    grid.replaceChildren(fragment);
    if (focusedId) restoreCardFocus([...grid.children].find((entry) => entry.dataset.acWishlistItem === focusedId), focusSelector);
    state.more.hidden = items.length <= state.limit;
    grid.setAttribute('aria-busy', String(Boolean(visible.length)));
    status.textContent = visible.length ? text('loading') : '';
    if (!visible.length) { grid.setAttribute('aria-busy', 'false'); return; }
    let cursor = 0;
    let failures = 0;
    const worker = async () => {
      while (cursor < visible.length && !controller.signal.aborted) {
        const position = cursor++;
        const item = visible[position];
        const result = await loadProduct(item, controller.signal);
        if (!result || controller.signal.aborted || generation !== state.generation || !page.isConnected) return;
        if (result.status === 'error') failures += 1;
        const old = [...grid.children].find((entry) => entry.dataset.acWishlistItem === item.id);
        if (!old) continue;
        const focusSelector = focusedControl(old);
        const replacement = card(item, result, page, position);
        old.replaceWith(replacement);
        restoreCardFocus(replacement, focusSelector);
      }
    };
    await Promise.all(Array.from({ length: Math.min(4, visible.length) }, worker));
    if (controller.signal.aborted || generation !== state.generation) return;
    grid.setAttribute('aria-busy', 'false');
    status.textContent = '';
    error.hidden = !failures;
    errorMessage.textContent = failures ? text('partialError') : '';
    state.lastRefresh = Date.now();
  }

  function initializePages() {
    document.querySelectorAll('[data-ac-wishlist-page]').forEach((page) => {
      if (pages.has(page)) return;
      const more = node('button', 'ac-wl-page__text-button ac-wl-more', text('loadMore'));
      more.type = 'button';
      more.hidden = true;
      const state = { limit: 24, generation: 0, more, controller: null };
      page.querySelector('[data-ac-wishlist-grid]').after(more);
      more.addEventListener('click', () => {
        const firstNewIndex = state.limit;
        state.limit += 24;
        renderPage(page, state, getItems());
        page.querySelectorAll('[data-ac-wishlist-item]')[firstNewIndex]?.querySelector('a,button')?.focus();
      });
      page.querySelector('[data-ac-wishlist-retry]').addEventListener('click', () => {
        cache.clear();
        renderPage(page, state, getItems());
        (page.querySelector('[data-ac-wishlist-item] a, [data-ac-wishlist-item] button') || page.querySelector('[data-ac-wishlist-empty-heading]'))?.focus({ preventScroll: true });
      });
      pages.set(page, state);
      renderPage(page, state, getItems());
    });
  }

  class WishlistToggle extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      const button = this.querySelector('[data-ac-wishlist-toggle]');
      if (!button) return;
      const context = this.dataset.acWishlistContext;
      if (context === 'product' && (this.closest('product-card, quick-add-dialog, dialog') || String(config.productId) !== button.dataset.productId)) return;
      this.controller = new AbortController();
      this.hidden = false;
      button.addEventListener('click', (event) => {
        event.preventDefault();
        toggle({ id: button.dataset.productId, handle: button.dataset.productHandle, title: button.dataset.productTitle, vendor: button.dataset.productVendor, image: button.dataset.productImage });
      }, { signal: this.controller.signal });
      updateControls();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
    }
  }

  window.ACWishlist = Object.freeze({ getItems, toggle, remove, undo, refresh });
  if (!customElements.get('ac-wishlist-toggle')) customElements.define('ac-wishlist-toggle', WishlistToggle);
  window.addEventListener('storage', (event) => {
    if (event.key !== null && !event.key.startsWith(prefix)) return;
    cancelAnimationFrame(refreshFrame);
    refreshFrame = requestAnimationFrame(refresh);
  });
  window.addEventListener('pageshow', () => refresh());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    updateControls();
    for (const [page, state] of pages) {
      if (Date.now() - (state.lastRefresh || 0) > 300000) renderPage(page, state, getItems());
    }
  });
  document.addEventListener('shopify:section:load', () => { initializePages(); updateControls(); });
  document.addEventListener('shopify:section:unload', () => {
    requestAnimationFrame(() => {
      for (const [page, state] of pages) if (!page.isConnected) { state.controller?.abort(); pages.delete(page); }
    });
  });
  const observer = new MutationObserver((mutations) => {
    if (!mutations.some((mutation) => [...mutation.addedNodes].some((added) => added.nodeType === 1 && (added.matches?.('[data-ac-wishlist-link]') || added.querySelector?.('[data-ac-wishlist-link]'))))) return;
    updateControls();
  });
  observer.observe(document.body, { childList: true, subtree: true });
  updateControls();
  initializePages();
}
